/* eslint-disable react/jsx-props-no-spreading */
import isSameDay from 'date-fns/isSameDay';
import parseISO from 'date-fns/parseISO';
import roundToNearestMinutes from 'date-fns/roundToNearestMinutes';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import React, { useMemo } from 'react';
import { FormattedDate } from 'react-intl';

dayjs.extend(isBetween);

export const uniqueMonths = (dates) => {
    const months = useMemo(() => {
        if (dates === null) {
            return [];
        }

        const formattedMonths = dates?.map((date) => dayjs(date).format('YYYY-MM'));
        const uniqueMonthsSet = [...new Set(formattedMonths)];
        return uniqueMonthsSet;
    }, [dates]);
    return months;
};

export const isDayInMonth = (date, month) => {
    const currentDate = dayjs(date);
    const currentMonth = dayjs(month);

    if (currentDate.isSame(currentMonth, 'month')) {
        return true;
    }
    return false;
};

export const getDatesInMonth = (dates, month) => {
    const monthDates = useMemo(
        () =>
            dates.reduce((days, date) => {
                const inMonth = isDayInMonth(date, month);

                if (inMonth) {
                    return [...days, date];
                }
                return days;
            }, []),

        [dates, month],
    );
    return monthDates;
};

export const useDateGroups = (items = null, groupFormat = 'YYYY-MM-DD', format = null) => {
    const groups = useMemo(() => {
        if (items === null) {
            return null;
        }

        return items.reduce((map, item) => {
            const date = dayjs(item.startDate);
            const id = date.format(groupFormat);
            const index = map.findIndex((it) => it.id === id);

            if (index === -1) {
                return [
                    ...map,
                    {
                        id,
                        date,
                        title: <FormattedDate value={date.toDate()} {...format} />,
                        items: [item],
                        type: 'date',
                    },
                ];
            }

            return [
                ...map.slice(0, index),
                {
                    ...map[index],
                    items: [...map[index].items, item],
                },
                ...map.slice(index + 1),
            ];
        }, []);
    }, [items, groupFormat, format]);

    return groups;
};

export function useParseDate(date = null) {
    const parsed = useMemo(
        () =>
            // eslint-disable-next-line no-nested-ternary
            date instanceof Date ? date : date !== null ? parseISO(date) : null,
        [date],
    );
    return parsed;
}

export function useParseDates(
    startDate = null,
    endDate = null,
    { roundedStart = false, roundedEnd = true } = {},
) {
    const parsedStartDate = useParseDate(startDate);
    const parsedEndDate = useParseDate(endDate);

    const finalStartDate =
        parsedStartDate !== null && roundedStart
            ? roundToNearestMinutes(parsedStartDate, { nearestTo: 5, roundingMethod: 'ceil' })
            : parsedStartDate;

    const finalEndDate =
        parsedEndDate !== null && roundedEnd
            ? roundToNearestMinutes(parsedEndDate, { nearestTo: 5, roundingMethod: 'ceil' })
            : parsedEndDate;

    const endIsSameDay =
        finalStartDate !== null && finalEndDate !== null
            ? isSameDay(finalStartDate, finalEndDate)
            : false;

    return {
        startDate: finalStartDate,
        endDate: finalEndDate,
        endIsSameDay,
    };
}

export default { uniqueMonths, isDayInMonth, getDatesInMonth, useParseDate, useParseDates };
